// ============================================
// FILE: /frontend/src/pages/MyPriceAlertsPage.jsx
// PURPOSE: View and manage user's price alerts
// ============================================

import { useState, useEffect } from 'react';
import { Bell, Loader2, Trash2, TrendingDown, CheckCircle } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { priceAlertsAPI } from '../api/priceAlerts.api';
import { useNavigate } from 'react-router-dom';

export default function MyPriceAlertsPage() {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchAlerts();
    }
  }, [user]);

  const fetchAlerts = async () => {
    try {
      const response = await priceAlertsAPI.getUserPriceAlerts(user.id);
      setAlerts(response.alerts);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load price alerts.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (alertId) => {
    try {
      await priceAlertsAPI.deletePriceAlert(alertId);
      setAlerts(alerts.filter((a) => a.id !== alertId));
      toast({
        title: 'Removed',
        description: 'Price alert removed successfully.',
      });
    } catch (error) {
      console.error('Failed to delete alert:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to remove alert.',
      });
    }
  };

  const calculatePriceDrop = (alert) => {
    const original = parseFloat(alert.originalPrice);
    const current = parseFloat(alert.currentPrice);
    const dropAmount = original - current;
    const dropPercent = ((dropAmount / original) * 100).toFixed(1);
    return { dropAmount, dropPercent };
  };

  if (authLoading || loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <Card>
            <CardHeader className="p-4 sm:p-5 md:p-6">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl md:text-2xl">
                <Bell className="w-5 h-5 sm:w-6 sm:h-6" />
                My Price Alerts ({alerts.length})
              </CardTitle>
            </CardHeader>

            <CardContent className="p-3 sm:p-4 md:p-6">
              {alerts.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Bell className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-muted-foreground mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">
                    No Price Alerts
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                    Set alerts on listings to get notified when prices drop
                  </p>
                  <Button onClick={() => navigate('/vehicles')} className="h-9 sm:h-10 text-xs sm:text-sm">
                    Browse Listings
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {/* Active Alerts */}
                  {alerts.filter(a => !a.triggered).length > 0 && (
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3">
                        Active Alerts ({alerts.filter(a => !a.triggered).length})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        {alerts.filter(a => !a.triggered).map((alert) => (
                          <Card key={alert.id} className="overflow-hidden">
                            <div
                              onClick={() => navigate(`/listing/${alert.listing.id}`)}
                              className="cursor-pointer"
                            >
                              {/* Image */}
                              <div className="w-full h-40 sm:h-48 bg-gray-200">
                                {alert.listing.imageUrl ? (
                                  <img
                                    src={alert.listing.imageUrl}
                                    alt={alert.listing.title}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs sm:text-sm">
                                    No Image
                                  </div>
                                )}
                              </div>

                              {/* Content */}
                              <div className="p-3 sm:p-4">
                                <h4 className="font-semibold text-sm sm:text-base line-clamp-1">
                                  {alert.listing.title}
                                </h4>
                                <div className="mt-2 space-y-1">
                                  <div className="flex justify-between text-xs sm:text-sm">
                                    <span className="text-muted-foreground">Original:</span>
                                    <span className="font-medium line-through">
                                      KES {parseFloat(alert.originalPrice).toLocaleString()}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-xs sm:text-sm">
                                    <span className="text-muted-foreground">Current:</span>
                                    <span className="font-bold text-primary">
                                      KES {parseFloat(alert.currentPrice).toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                                <Badge variant="secondary" className="mt-2 text-[10px] sm:text-xs">
                                  <Bell className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1" />
                                  Alert at {alert.alertThreshold}% drop
                                </Badge>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="border-t px-3 sm:px-4 py-2 sm:py-3">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(alert.id)}
                                className="w-full h-8 sm:h-9 text-xs sm:text-sm"
                              >
                                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5" />
                                Remove Alert
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Triggered Alerts */}
                  {alerts.filter(a => a.triggered).length > 0 && (
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3 flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                        Price Drops ({alerts.filter(a => a.triggered).length})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        {alerts.filter(a => a.triggered).map((alert) => {
                          const { dropAmount, dropPercent } = calculatePriceDrop(alert);
                          return (
                            <Card key={alert.id} className="overflow-hidden border-2 border-green-200">
                              <div
                                onClick={() => navigate(`/listing/${alert.listing.id}`)}
                                className="cursor-pointer"
                              >
                                {/* Success Badge */}
                                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-2 flex items-center justify-center gap-2">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-xs sm:text-sm font-semibold">
                                    PRICE DROPPED!
                                  </span>
                                </div>

                                {/* Image */}
                                <div className="w-full h-40 sm:h-48 bg-gray-200">
                                  {alert.listing.imageUrl ? (
                                    <img
                                      src={alert.listing.imageUrl}
                                      alt={alert.listing.title}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs sm:text-sm">
                                      No Image
                                    </div>
                                  )}
                                </div>

                                {/* Content */}
                                <div className="p-3 sm:p-4">
                                  <h4 className="font-semibold text-sm sm:text-base line-clamp-1">
                                    {alert.listing.title}
                                  </h4>
                                  <div className="mt-2 space-y-1">
                                    <div className="flex justify-between text-xs sm:text-sm">
                                      <span className="text-muted-foreground">Was:</span>
                                      <span className="font-medium line-through">
                                        KES {parseFloat(alert.originalPrice).toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex justify-between text-xs sm:text-sm">
                                      <span className="text-muted-foreground">Now:</span>
                                      <span className="font-bold text-green-600">
                                        KES {parseFloat(alert.currentPrice).toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex justify-between text-xs sm:text-sm pt-1 border-t">
                                      <span className="font-semibold">You Save:</span>
                                      <span className="font-bold text-green-600">
                                        KES {dropAmount.toLocaleString()} ({dropPercent}%)
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="border-t px-3 sm:px-4 py-2 sm:py-3 flex gap-2">
                                <Button
                                  onClick={() => navigate(`/listing/${alert.listing.id}`)}
                                  className="flex-1 h-8 sm:h-9 text-xs sm:text-sm"
                                >
                                  View Now
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(alert.id)}
                                  className="h-8 sm:h-9 text-xs sm:text-sm"
                                >
                                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                </Button>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
